// One canonical builder for DYNAMICALLY-CREATED gl-modal shells.
//
// Several helpers/controllers need a gl-modal that has no layout partial behind
// it — they build the DOM at call time, show it, and destroy it on dismissal.
// This module owns their whole lifecycle exactly once — skeleton markup, the
// stacking z-index, `new bootstrap.Modal`, listener bookkeeping, `dispose()` +
// `remove()` on `hidden.bs.modal`, and the stacked-backdrop z-index bump; the
// callers are declarative configs over it (see lib/custom_confirm.js and
// controllers/org_confirm_gate_controller.js).
//
// Escape / backdrop / × dismissal all funnel through Bootstrap's own hide(),
// which surfaces as `hidden.bs.modal` — so `onHidden` is the single dismissal
// hook regardless of how the user closed the dialog.
//
// Bootstrap's hide() is a silent no-op while the modal is still fading IN
// (`_isTransitioning`). A Confirm or Cancel landing inside that window would
// be swallowed, and with the footer already disabled as "hiding" the dialog
// would stick open with dead buttons (#14135). So shell.hide() defers to
// `shown.bs.modal` when asked
// during the open transition, and the shell's own dismiss buttons (Cancel, ×)
// go through it rather than Bootstrap's delegated handler alone.
//
// This is for shells with NO server-rendered partial. A modal that already
// exists in the DOM (a HAML/Turbo-frame modal) is not this module's business.

// Stacking values. A dynamically-built shell must sit above any modal already
// open (Bootstrap's default modal z-index is 1055, backdrop 1050).
const STACKED_MODAL_Z_INDEX = '1070'
const STACKED_BACKDROP_Z_INDEX = '1065'

// Monotonic suffix so two shells mounted inside the same millisecond can never
// collide on ids (`Date.now()` alone could, which would cross-wire the
// `<label for>` / confirm-button ids of two stacked dialogs).
let mountSequence = 0

/**
 * Escape a string for interpolation into markup.
 *
 * textContent-based on purpose: it encodes &, <, > but NOT quotes. Values that
 * land inside an ATTRIBUTE must therefore never go through here — assign them
 * as DOM properties after mount instead (see showPromptModal's inputEl.value).
 *
 * @param {*} str
 * @returns {string}
 */
export function escapeHTML(str) {
  const div = document.createElement('div')
  div.textContent = str
  return div.innerHTML
}

/**
 * Raise the topmost backdrop when modals are stacked, so the new shell's
 * backdrop covers the modal underneath it instead of sitting behind both.
 * No-op with a single backdrop — the default z-index is already correct.
 */
export function bumpStackedBackdropZIndex() {
  const backdrops = document.querySelectorAll('.modal-backdrop')
  if (backdrops.length > 1) {
    backdrops[backdrops.length - 1].style.zIndex = STACKED_BACKDROP_Z_INDEX
  }
}

function buttonHTML(button, refId) {
  const attributes = ['type="button"', `class="${button.className}"`]
  if (button.ref) attributes.push(`id="${refId(button.ref)}"`)
  if (button.dismiss) attributes.push('data-bs-dismiss="modal"')
  if (button.disabled) attributes.push('disabled')
  if (button.autofocus) attributes.push('autofocus')

  const icon = button.icon ? `<i class="${button.icon} me-1"></i> ` : ''
  return `<button ${attributes.join(' ')}>${icon}${escapeHTML(button.text)}</button>`
}

/**
 * Build, mount, and show a gl-modal shell. Synchronous — the element is in the
 * document and `show()` has been called by the time this returns.
 *
 * @param {Object} options
 * @param {string} [options.idPrefix='gl-modal'] - Prefix for the generated element id
 * @param {string} [options.title=''] - Header title (plain text, escaped)
 * @param {string} [options.headerIcon='ph ph-question'] - Phosphor icon class for the header
 * @param {string} [options.headerClass=''] - Intent class, e.g. 'gl-modal-header--danger'
 * @param {string|function} [options.bodyHTML=''] - Body markup, inserted UNESCAPED (escape
 *   caller-controlled values yourself). As a function it receives `{ id, refId }` so body
 *   markup can reference generated ids (`refId('input')` → `<modal id>-input`).
 * @param {Array<Object>} [options.buttons=[]] - Footer buttons, left to right. Each:
 *   `{ text, className, ref?, icon?, dismiss?, disabled?, onClick? }`. `ref` gives the
 *   button the id `<modal id>-<ref>` and exposes it as `shell.buttons[ref]`; `onClick`
 *   receives the shell.
 * @param {boolean} [options.scrollable=false] - Cap the dialog at the viewport and scroll its
 *   body (Bootstrap `modal-dialog-scrollable`, the same as `gl_modal(scrollable: true)` in HAML),
 *   so a long body never pushes the footer off screen.
 * @param {string} [options.zIndex='1070'] - Stacking z-index for the shell
 * @param {Element} [options.container=document.body] - Element the shell mounts into.
 *   Bootstrap modals are position: fixed, so rendering is identical anywhere in the
 *   DOM — pass a Stimulus controller's `this.element` when the body markup carries
 *   `data-action` attributes that must bind to that controller (actions only bind
 *   inside the controller's element scope).
 * @param {function} [options.onMounted] - Called with the shell after the DOM exists and
 *   footer buttons are wired, but BEFORE the Bootstrap instance is created and shown.
 *   Use it to inject dynamic body content or set input properties. `shell.modal` is
 *   still null here.
 * @param {function} [options.onShown] - Called with the shell on `shown.bs.modal`
 * @param {function} [options.onHidden] - Called with the shell on `hidden.bs.modal`, AFTER
 *   the shell has torn itself down (listeners removed, instance disposed, element removed).
 *   This is the single hook for every dismissal path: confirm, cancel, ×, Esc, backdrop.
 * @returns {Object} shell — `{ id, element, modal, header, body, footer, buttons, refId,
 *   ref(name), on(target, event, handler), hide(), destroy() }`
 */
export function mountGlModalShell(options = {}) {
  const {
    idPrefix = 'gl-modal',
    title = '',
    headerIcon = 'ph ph-question',
    headerClass = '',
    bodyHTML = '',
    buttons = [],
    scrollable = false,
    zIndex = STACKED_MODAL_Z_INDEX,
    container = document.body,
    onMounted = null,
    onShown = null,
    onHidden = null,
  } = options

  mountSequence += 1
  const id = `${idPrefix}-${Date.now()}-${mountSequence}`
  const refId = (name) => `${id}-${name}`

  const bodyMarkup = typeof bodyHTML === 'function' ? bodyHTML({ id, refId }) : bodyHTML
  const footerMarkup = buttons.map((button) => buttonHTML(button, refId)).join('\n')

  container.insertAdjacentHTML(
    'beforeend',
    `
      <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered${scrollable ? ' modal-dialog-scrollable' : ''}">
          <div class="modal-content gl-modal">
            <div class="gl-modal-header ${headerClass}">
              <h5 class="gl-modal-title">
                <i class="${headerIcon} me-2"></i>
                ${escapeHTML(title)}
              </h5>
              <button type="button" class="gl-modal-close" data-bs-dismiss="modal" data-gl-tooltip="Close" data-gl-tooltip-pos="bottom" aria-label="Close"><i class="ph ph-x"></i></button>
            </div>
            <div class="gl-modal-body">${bodyMarkup}</div>
            <div class="gl-modal-footer">${footerMarkup}</div>
          </div>
        </div>
      </div>
    `
  )

  // The just-inserted element, rather than a lookup by id — the shell owns its
  // own node and never has to trust the document to disambiguate.
  const element = container.lastElementChild

  const listeners = []
  let destroyed = false
  // Set the moment a dismissal starts. Bootstrap keeps the footer live through
  // its fade, so without this a quick second click (Alternate then Confirm on
  // showConfirmModal, #13764) would run a second onClick and overwrite the
  // outcome the first one recorded. The first terminal action wins.
  let hiding = false
  // True between Bootstrap's `show.bs.modal` and `shown.bs.modal`: its hide()
  // ignores callers during that window, so a hide requested then is parked
  // and replayed the moment the open transition ends.
  let opening = false
  let hideWhenShown = false

  const markHiding = () => {
    if (hiding) return
    hiding = true
    element.querySelectorAll('.gl-modal-footer button').forEach((button) => {
      button.disabled = true
    })
  }

  const shell = {
    id,
    element,
    modal: null,
    header: element.querySelector('.gl-modal-header'),
    body: element.querySelector('.gl-modal-body'),
    footer: element.querySelector('.gl-modal-footer'),
    buttons: {},
    refId,

    // Look up an element the body/footer markup created via `refId`.
    ref(name) {
      return element.querySelector(`[id="${refId(name)}"]`)
    },

    // Register a listener the shell will remove on teardown.
    on(target, event, handler) {
      if (!target) return
      target.addEventListener(event, handler)
      listeners.push([target, event, handler])
    },

    // No-op once torn down: Bootstrap's hide() on a disposed instance throws,
    // and a caller's handler can legitimately fire after teardown (e.g. a
    // controller disconnected by Turbo while its dialog was still open).
    hide() {
      if (destroyed || !shell.modal) return
      markHiding()
      if (opening) {
        hideWhenShown = true
        return
      }
      shell.modal.hide()
    },

    // Idempotent teardown. Safe to call from a caller's own cleanup path even
    // though the shell already ran it on `hidden.bs.modal`.
    destroy() {
      if (destroyed) return
      destroyed = true
      listeners.forEach(([target, event, handler]) => target.removeEventListener(event, handler))
      listeners.length = 0
      if (shell.modal) shell.modal.dispose()
      element.remove()
    },
  }

  buttons.forEach((button) => {
    if (!button.ref) return
    shell.buttons[button.ref] = shell.ref(button.ref)
  })

  buttons.forEach((button, index) => {
    if (!button.onClick) return
    const buttonEl = button.ref
      ? shell.buttons[button.ref]
      : shell.footer.querySelectorAll('button')[index]
    shell.on(buttonEl, 'click', () => {
      if (hiding || destroyed) return
      button.onClick(shell)
    })
  })

  // The shell's own dismiss controls (a `dismiss: true` footer button, the ×)
  // keep `data-bs-dismiss` for Bootstrap's delegated handler, but ALSO close
  // through shell.hide() so a click during the open transition is deferred
  // rather than dropped. Order is safe either way: this listener runs first on
  // the way up, and Bootstrap's then finds the modal already hiding (or the
  // button disabled) and returns.
  element.querySelectorAll('[data-bs-dismiss="modal"]').forEach((dismisser) => {
    shell.on(dismisser, 'click', () => shell.hide())
  })

  if (onMounted) onMounted(shell)

  element.style.zIndex = zIndex

  // A shell mounted INSIDE another modal (container inside a .modal element —
  // e.g. the notifications channel editor, whose data-actions must bind to its
  // controller) is trapped in that host modal's stacking context: no z-index
  // can lift it above a body-level element that out-stacks the host. Bootstrap
  // appends backdrops to <body>, so the shell's own bumped backdrop (1065)
  // would paint ABOVE the host modal (1055) and the shell alike — a full-screen
  // click shield over an unusable UI. Run backdrop-free instead and dim via the
  // shell's full-screen .modal element (same stacking context, so it can never
  // cover its own dialog). The Escape guard stops one keypress from bubbling to
  // the host modal element and closing both dialogs at once — Bootstrap's own
  // Escape handler on the shell element runs first, so the shell still closes.
  const nestedInOpenModal = container !== document.body && !!container.closest('.modal')
  shell.modal = new window.bootstrap.Modal(element, nestedInOpenModal ? { backdrop: false } : {})
  if (nestedInOpenModal) {
    element.classList.add('gl-modal-shell--nested')
    shell.on(element, 'keydown', (event) => {
      if (event.key === 'Escape') event.stopPropagation()
    })
  }

  // Esc, backdrop and data-bs-dismiss bypass shell.hide(), so the same guard
  // hangs off Bootstrap's own start-of-hide event.
  shell.on(element, 'hide.bs.modal', markHiding)
  shell.on(element, 'hidden.bs.modal', () => {
    shell.destroy()
    if (onHidden) onHidden(shell)
  })
  // Bootstrap fires show.bs.modal synchronously inside show() and shown.bs.modal
  // once its transition ends; a hide parked in between replays here, ahead of
  // the caller's onShown, so that hook sees a dialog already on its way out.
  shell.on(element, 'show.bs.modal', () => {
    opening = true
  })
  shell.on(element, 'shown.bs.modal', () => {
    opening = false
    if (hideWhenShown && !destroyed) shell.modal.hide()
  })
  if (onShown) shell.on(element, 'shown.bs.modal', () => onShown(shell))

  shell.modal.show()

  if (!nestedInOpenModal) requestAnimationFrame(bumpStackedBackdropZIndex)

  return shell
}
